import { fetchEventSource } from '@microsoft/fetch-event-source';
import React, { Component, FC } from 'react';
import { conversationHelper, historyService } from '../helpers';
import { Character, Message, MessageType } from '../models';
import { conversationHubResource } from '../resource';

interface InputProps {
  character: Character | null;
  idConversation?: string;
  idCustomer: string;
  idExpert: string;
}

interface States {
  discussion: Message[];
  loading: boolean; // indicates whether the history is loading
}

interface OutputProps {
  character: Character | null;
  discussion: Message[];
  loading: boolean; // exposed to the wrapped component
  send: (
    conversation: string,
    message: string,
    type?: MessageType,
    author?: string,
    authorIdentifier?: string,
    authorType?: string,
    conversationBetween?: object,
  ) => void;
}

export const withConversation = (WrappedComponent: FC<OutputProps>) =>
  class Conversation extends Component<InputProps, States> {
    private buffer: Message[] = [];
    private eventSource!: EventSource;
    private lastEventId?: string; // id of the LAST received event (history)
    private timer?: any;

    constructor(props: InputProps) {
      super(props);

      this.state = { discussion: [], loading: false };

      this.send = this.send.bind(this);
    }

    /**
     * Groups real-time messages to limit re-renders.
     */
    addWithBuffer({ data, lastEventId }: any) {
      const message = JSON.parse(data) as Message;

      const messageExists = this.state.discussion.some(
        (msg: Message) =>
          msg.message === message.message &&
          msg.author === message.author &&
          msg.date === message.date,
      );

      if (!messageExists) {
        this.buffer.push(message);
      }

      if (this.timer) {
        clearTimeout(this.timer);
      }

      this.timer = setTimeout(() => {
        this.setState({
          discussion: [...this.state.discussion, ...this.buffer], // append (old -> recent)
        });

        if (this.props.idCustomer !== '{}') {
          conversationHelper.setLastId(message['@id'], lastEventId);
        }

        this.buffer = [];
      }, 500);
    }

    closeEventSource() {
      if (this.eventSource) {
        this.eventSource.close();
      }
      // Reset discussion when switching conversations
      this.setState({ discussion: [] });

      // Clear author name cache
      historyService.clearCache();
    }

    componentDidMount() {
      const { idCustomer, idExpert } = this.props;
      this.loadHistoryChunk(idExpert, idCustomer);
    }

    componentDidUpdate(prevProps: InputProps) {
      if (
        prevProps.idCustomer !== this.props.idCustomer ||
        prevProps.idExpert !== this.props.idExpert
      ) {
        this.setState({ loading: true });
        this.closeEventSource();
        this.loadHistoryChunk(this.props.idExpert, this.props.idCustomer);
      }
    }

    componentWillUnmount() {
      this.closeEventSource();
      if (this.timer) {
        clearTimeout(this.timer);
      }
    }

    /**
     * Loads history from SseRecord database and then connects to EventSource for real-time messages.
     */
    loadHistoryChunk = async (
      idExpert: string,
      idCustomer: string,
      lastEventId?: string,
      chunkNumber: number = 1,
    ) => {
      try {
        // Show loading state
        this.setState({ loading: true });

        // Only load history if we have a conversation ID
        if (this.props.idConversation) {
          // Build conversation IRI from idConversation
          // Handle both full IRI ("/api/conversations/123") and partial IRI ("/conversations/123")
          let conversationIri = this.props.idConversation;
          if (conversationIri.startsWith('/conversations/')) {
            conversationIri = `/api${conversationIri}`;
          } else if (!conversationIri.startsWith('/api/conversations/')) {
            // If it's just an ID like "123", build the full IRI
            conversationIri = `/api/conversations/${conversationIri}`;
          }

          // Load history from SseRecord database
          const historyMessages =
            await historyService.loadHistory(conversationIri);

          // Set discussion state with loaded history
          if (historyMessages.length > 0) {
            this.setState({ discussion: historyMessages });
          }

          // Get the most recent SseRecord ID to use as lastEventId
          // This prevents EventSource from re-delivering messages we already have
          const lastHistoryEventId =
            historyService.getLastEventId(historyMessages);

          // Connect to EventSource for real-time messages
          // Use the last history event ID to prevent duplicates
          this.setEventSource(idExpert, idCustomer, lastHistoryEventId);
        } else {
          // No conversation ID provided - skip history loading
          // This is used for ConversationList which listens to all conversations
          const skipHistoryId = 'skip-history-' + Date.now();
          this.setEventSource(idExpert, idCustomer, skipHistoryId);
        }

        // Hide loading state
        this.setState({ loading: false });
      } catch (error) {
        console.error('Error loading conversation history:', error);

        // Even if history loading fails, connect to EventSource for real-time messages
        // Use a high lastEventID to skip all history (fallback behavior)
        const skipHistoryId = 'skip-history-' + Date.now();
        this.setEventSource(idExpert, idCustomer, skipHistoryId);

        this.setState({ loading: false });
      }
    };

    render() {
      return (
        <WrappedComponent
          character={this.props.character}
          discussion={this.state ? this.state.discussion : []}
          loading={this.state.loading}
          send={this.send}
        />
      );
    }

    send(
      conversation: string,
      message: string,
      type: MessageType = MessageType.MESSAGE,
      author?: string,
      authorIdentifier?: string,
      authorType?: string,
      conversationBetween?: object,
    ): Promise<string> {
      const { idCustomer, idExpert } = this.props;
      return conversationHubResource.sendMessage(
        `/conversations/${idExpert}/${idCustomer}`,
        { idCustomer, idExpert },
        conversation,
        type,
        message,
        author,
        authorIdentifier,
        authorType,
        conversationBetween,
      );
    }

    /**
     * Enables real-time listening after loading history,
     * asking the hub to send only what follows `sinceId`.
     */
    setEventSource(idExpert: string, idCustomer: string, sinceId?: string) {
      const urlObj = new URL(
        conversationHubResource.getEventUrl({ idCustomer, idExpert }),
      );
      if (sinceId) {
        // Mercure supports lastEventID as a query string parameter
        urlObj.searchParams.set('lastEventID', sinceId);
      } else {
        // console.warn(
        //   'ℹ️ setEventSource started without sinceId — a small overlap is possible.',
        // );
      }

      this.eventSource = new EventSource(urlObj.toString(), {
        withCredentials: true,
      });

      this.eventSource.onmessage = (evt: MessageEvent) => {
        // No seenIds here: let addWithBuffer deduplicate by (message/author/date)
        const id = (evt as any).lastEventId as string | undefined;
        this.addWithBuffer({ data: (evt as any).data, lastEventId: id });
      };
    }
  };
